import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import { KEYS } from 'helpers/react-query.config';

export type ApiStatusChangeReturn = {
  /**
   * Mutation to update a ticket's status to "On Site".
   */
  updateTicketOnSite: UseMutationResult<
    unknown,
    unknown,
    { id: number; comment: string }
  >;

  /**
   * Mutation to update a ticket's status to "Scheduled".
   */
  updateTicketToScheduled: UseMutationResult<
    unknown,
    unknown,
    { ticketId: number; comment: string; scheduledApproved: boolean }
  >;
};

/**
 * A custom hook that provides functionality to update the status of tickets
 * in the API. It includes methods to update a ticket to "On Site" and to
 * schedule a ticket. The hook utilizes global context for notifications
 * and manages API calls with React Query.
 *
 * @returns {ApiStatusChangeReturn} An object containing two mutation functions.
 *
 * @example
 * const { updateTicketOnSite, updateTicketToScheduled } = useApiStatusChange();
 *
 * updateTicketOnSite.mutate({ id: 123, comment: 'Ticket is now on site.' });
 * updateTicketToScheduled.mutate({ ticketId: 123, comment: 'Scheduled for review.', scheduledApproved: true });
 */

const useApiStatusChange = (): ApiStatusChangeReturn => {
  const { showSuccess, showApiError } = useGlobalContext();
  const { ticket: ticketApi } = useApiContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation('global');

  const updateTicketOnSite = useMutation(
    ({ id, comment }: { id: number; comment: string }) =>
      ticketApi.tickets.updateTicketsToOnSite({
        ticketsId: [String(id)],
        comment
      }),
    {
      onSuccess: async (data) => {
        if (data.data.data[0].isError) {
          throw new Error(data.data.data[0].message);
        }

        await Promise.all([
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.detail(+data.data.data[0].ticketId)
          ),
          queryClient.invalidateQueries(KEYS.ticket.logbook.list()),
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.getAvailableStatuses()
          )
        ]);

        showSuccess(
          `${t('ticket.changeStatus.updatedTicketSuccess')} ${
            data.data.data[0].ticketId
          }`
        );
      },
      onError: (error) => {
        showApiError(undefined, error.message as string);
      }
    }
  );

  const updateTicketToScheduled = useMutation(
    ({
      ticketId,
      comment,
      scheduledApproved
    }: {
      ticketId: number;
      comment: string;
      scheduledApproved: boolean;
    }) =>
      ticketApi.tickets.meUpdateTicketsToSchedueled({
        TicketId: String(ticketId),
        comment,
        scheduledApproved
      }),
    {
      onSuccess: async ({ data }) => {
        await Promise.all([
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.detail(+data?.data?.ticketId)
          ),
          queryClient.invalidateQueries(KEYS.ticket.logbook.list()),
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.getAvailableStatuses()
          ),
          queryClient.invalidateQueries(KEYS.ticket.tickets.generalSearch())
        ]);

        showSuccess(
          `${t('ticket.changeStatus.updatedTicketSuccess')} ${
            data?.data?.ticketId
          }`
        );
      },
      onError: (error) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      }
    }
  );

  return {
    updateTicketOnSite,
    updateTicketToScheduled
  };
};

export default useApiStatusChange;
